import React, { createContext, useContext, useEffect, useMemo } from "react";
import { createGeneratedDocument } from "../domain/entities";
import { useFoundation } from "../application/foundation";
import { useShipSettings } from "./ShipSettingsContext";
import { useUserContext } from "./UserContext";
import { useVoyageContextV2 } from "./VoyageContextV2";
import { useNotification } from "./NotificationContext";
import { PRINT_DOCUMENT_CREATED_EVENT } from "../print-v2/core/renderPrintDocument";

const DocumentArchiveContext = createContext(null);
const DOCUMENT_RETENTION_DAYS = 365;
const MAX_DOCUMENTS_PER_VESSEL = 200;

const toSnapshot = (value) => JSON.parse(JSON.stringify(value || {}));

/** Persists local Print V2 artifacts without enqueueing a cloud operation. */
export const DocumentArchiveProvider = ({ children }) => {
  const { repositories } = useFoundation();
  const { shipData } = useShipSettings();
  const { user } = useUserContext();
  const { currentVoyage, selectedTripId, selectedCargoId } = useVoyageContextV2();
  const { showNotification } = useNotification();

  useEffect(() => {
    const archiveDocument = async (event) => {
      const { title, html, templateId, viewModel } = event.detail || {};
      const vesselId = currentVoyage?.vesselId || shipData?.id;
      const organizationId = currentVoyage?.organizationId || shipData?.organizationId;

      if (!vesselId || !organizationId || !templateId || !title || !html) {
        showNotification("Document was created but could not be archived locally", "warning");
        return;
      }

      try {
        await repositories.generatedDocuments.put(
          createGeneratedDocument({
            organizationId,
            vesselId,
            voyageId: currentVoyage?.id || null,
            tripId: selectedTripId || null,
            cargoId: selectedCargoId || null,
            templateId,
            documentTitle: title,
            generatedByUserId: user?.id || user?.userId || null,
            sourceRevision: currentVoyage?.revision || null,
            viewModelSnapshot: toSnapshot(viewModel),
            html,
          })
        );
        await repositories.generatedDocuments.applyRetention({
          vesselId,
          maximumRecords: MAX_DOCUMENTS_PER_VESSEL,
          deleteBefore: new Date(
            Date.now() - DOCUMENT_RETENTION_DAYS * 24 * 60 * 60 * 1000
          ).toISOString(),
        });
        showNotification("Document archived locally", "success");
      } catch (error) {
        console.error("Failed to archive generated document", error);
        showNotification("Document was created but could not be archived locally", "warning");
      }
    };

    window.addEventListener(PRINT_DOCUMENT_CREATED_EVENT, archiveDocument);
    return () => window.removeEventListener(PRINT_DOCUMENT_CREATED_EVENT, archiveDocument);
  }, [
    currentVoyage,
    repositories.generatedDocuments,
    selectedCargoId,
    selectedTripId,
    shipData?.id,
    shipData?.organizationId,
    showNotification,
    user?.id,
    user?.userId,
  ]);

  const value = useMemo(
    () => ({
      listGeneratedDocuments: (vesselId) =>
        repositories.generatedDocuments.listByVesselId(vesselId),
      deleteGeneratedDocument: (documentId) =>
        repositories.generatedDocuments.delete(documentId),
      clearGeneratedDocuments: (vesselId) =>
        repositories.generatedDocuments.deleteByVesselId(vesselId),
    }),
    [repositories.generatedDocuments]
  );

  return (
    <DocumentArchiveContext.Provider value={value}>
      {children}
    </DocumentArchiveContext.Provider>
  );
};

export const useDocumentArchive = () => {
  const context = useContext(DocumentArchiveContext);
  if (!context) {
    throw new Error("useDocumentArchive must be used within DocumentArchiveProvider");
  }
  return context;
};
